// src/components/grammar/GrammarLearningContainer.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate'; // <-- ADD THIS IMPORT
import { ArrowLeft, Lock, ChevronLeft, ChevronRight, Dumbbell, Grid, Home, ArrowUpRight } from 'lucide-react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { logGrammarPractice, logGrammarPillarProgress } from '../../utils/activityLogger';
import subscriptionService from '../../services/subscriptionService';
import adminService from '../../services/adminService';
import freeAttemptService from '../../services/freeAttemptService';
import GrammarTabNavigation from './GrammarTabNavigation';
import GrammarPillarRenderer from './GrammarPillarRenderer';
import GrammarProgressTracker from './GrammarProgressTracker';
import { useTranslation } from 'react-i18next';
import grammarProgressService from '../../services/grammarProgressService';
import GrammarBottomNav from './GrammarBottomNav';
import { pillarIdToTopicMap, getGrammarTopicTitle } from '../../services/grammarTopicsMap';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

const ELEVATED_SURFACE =
  'relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)]';

const GrammarLearningContainer = ({ onBack, user, isMobile }) => {
  const { i18n } = useTranslation();
  const { level, examSlug } = useParams();
  const location = useLocation();
  const examPrefix = examSlug ? `/exams/${examSlug}` : '';
  const { t } = useTranslation();
  const getInitialPillarFromURL = () => {
    const params = new URLSearchParams(location.search);
    const pillarId = params.get('pillar');

    return pillarId ? parseInt(pillarId, 10) : 1; // Default to pillar 1 if not specified
  };

  // Core state management (now driven by URL)
  const [userLevel, setUserLevel] = useState(level ? level.toUpperCase() : 'B1');
  const [pillarProgress, setPillarProgress] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [grammarData, setGrammarData] = useState(null);
  const [activePillar, setActivePillar] = useState(getInitialPillarFromURL());
  const [userAnswers, setUserAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState({});
    const [currentContext, setCurrentContext] = useState({
    pillarId: null,
    categoryId: null,
    categoryName: null,
    practiceLevel: null,
    practiceLevelName: null,
    structureType: null
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const navigate = useLocalizedNavigate();
  const openSectionsMenuRef = useRef(null); // Ref to open sections menu from bottom nav

  // Free plan: ONE pass of the free grammar pillar. `grammarAttemptUsed` reflects
  // usage BEFORE this visit (the current pass stays fully usable); the attempt is
  // marked once the free user actually views grammar content.
  const [grammarAttemptUsed, setGrammarAttemptUsed] = useState(null); // null = loading
  const grammarMarkedRef = useRef(false);

  useEffect(() => {
    if (!user?.uid) { setGrammarAttemptUsed(false); return; }
    freeAttemptService.getFreeAttempts(user.uid)
      .then(attempts => setGrammarAttemptUsed(freeAttemptService.isGrammarUsed(attempts)));
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || grammarMarkedRef.current) return;
    if (adminService.isAdmin()) return;
    if (grammarAttemptUsed !== false) return; // still loading or already used
    if (subscriptionStatus === null) return;
    const isFreeUser = subscriptionStatus?.isFree || !subscriptionStatus?.isActive;
    if (!isFreeUser || !grammarData) return;
    grammarMarkedRef.current = true;
    freeAttemptService.markGrammarAttempt(user.uid);
  }, [user?.uid, subscriptionStatus, grammarData, grammarAttemptUsed]);

  // Load subscription status (only when user is logged in; when not logged in we leave null so we don't redirect)
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      if (user?.uid) {
        try {
          const status = await subscriptionService.getSubscriptionStatus(user.uid);
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Error loading subscription status:', error);
          setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
        }
      } else {
        // Don't set to free when no user, leave null so we never redirect and user can stay on selected lesson (lock UI still applies)
        setSubscriptionStatus(null);
      }
    };

    loadSubscriptionStatus();
  }, [user?.uid]);

    const detectCurrentContext = useCallback((pillarId, exerciseId) => {
    if (!grammarData || !pillarId || !exerciseId) return;

    const context = grammarProgressService.detectExerciseContext(
      grammarData,
      pillarId,
      exerciseId
    );

    if (context) {
      setCurrentContext({
        pillarId,
        ...context
      });
      console.log('🎯 Current context detected:', context);
    }
  }, [grammarData]);
  // Load grammar data and user progress
  useEffect(() => {
    const loadGrammarData = async () => {
      setIsLoading(true);
      const levelLower = userLevel.toLowerCase();
      // Determine which language file to load. German ('de') should use the English ('en') file.
      const langToLoad = i18n.language === 'de' ? 'en' : i18n.language;
      const defaultLang = 'en';

      try {
        let data;
        // 1. Try to fetch the language-specific grammar file (e.g., b1-grammar_fr.json)
        try {
          const response = await fetch(`/data/grammar-levels/${levelLower}-grammar_${langToLoad}.json`);
          if (!response.ok) {
            throw new Error(`Grammar file not found for language: ${langToLoad}`);
          }
          data = await response.json();
          console.log(`Successfully loaded '${langToLoad}' grammar for level ${userLevel}`);
        } catch (error) {
          console.warn(`Could not load '${langToLoad}' grammar for ${userLevel}. Falling back to English.`, error);

          // 2. If it fails, fall back to the default English file
          const fallbackResponse = await fetch(`/data/grammar-levels/${levelLower}-grammar_${defaultLang}.json`);
          if (!fallbackResponse.ok) {
            throw new Error('Failed to load default English grammar data.');
          }
          data = await fallbackResponse.json();
          console.log(`Loaded fallback '${defaultLang}' grammar for level ${userLevel}`);
        }

        setGrammarData(data);

        // This part for user progress remains the same
        if (user) {
          const progressRef = doc(db, 'users', user.uid, 'grammarProgress', levelLower);
          const progressDoc = await getDoc(progressRef);

          if (progressDoc.exists()) {
            setPillarProgress(progressDoc.data().pillars || {});
          } else {
            await setDoc(progressRef, {
              level: userLevel,
              pillars: {},
              startedAt: new Date(),
              lastUpdated: new Date()
            });
          }
        }
      } catch (error) {
        console.error(`CRITICAL: Error loading grammar data for ${userLevel}:`, error);
        setGrammarData(null); // Set to null on critical failure
      } finally {
        setIsLoading(false);
      }
    };

    loadGrammarData();
  }, [user, userLevel, i18n.language]);

  // Redirect only when we know the user is logged in AND free; never redirect when status is still loading (null) or when no user
  useEffect(() => {
    if (!user?.uid || subscriptionStatus === null || !grammarData || !activePillar) return;
    // Free users can only access the first pillar, only redirect when we've confirmed they're free
    if (!(subscriptionStatus?.isFree || !subscriptionStatus?.isActive)) return;
    const pillarIndex = grammarData.pillars?.findIndex(p => p.id === activePillar);
    if (pillarIndex === 0) return;
    const firstPillarId = grammarData.pillars?.[0]?.id;
    if (!firstPillarId) return;
    setActivePillar(firstPillarId);
    const newUrl = `${examPrefix}/lessons/${userLevel.toLowerCase()}?pillar=${firstPillarId}`;
    navigate(newUrl, { replace: true });
  // Intentionally omit navigate/examPrefix from deps: navigate from useLocalizedNavigate
  // is a new function every render, which would re-run this effect constantly and fight with URL sync.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, subscriptionStatus, grammarData, activePillar, userLevel]);

  // ADD debugging useEffect
  useEffect(() => {
    if (grammarData) {
      console.log(`=== ${userLevel} GRAMMAR DATA LOADED ===`);
      console.log('Total pillars:', grammarData.pillars?.length);
      console.log('Pillar IDs:', grammarData.pillars?.map(p => p.id));
      console.log('Pillar titles:', grammarData.pillars?.map(p => p.title));
      console.log('First pillar structure:', grammarData.pillars?.[0]);

      // Check if B2 structure
      if (grammarData.pillars?.[0]?.explanation?.categories) {
        console.log('✅ B2 structure detected!');
        console.log('Categories in first pillar:', grammarData.pillars[0].explanation.categories.length);
        console.log('First category:', grammarData.pillars[0].explanation.categories[0]?.name);
        console.log('First category practice:', grammarData.pillars[0].explanation.categories[0]?.practice);
      } else if (grammarData.pillars?.[0]?.practice) {
        console.log('✅ B1 structure detected!');
        console.log('Practice levels in first pillar:', grammarData.pillars[0].practice?.length);
      }
    }
  }, [grammarData, userLevel]);

  // Update activePillar when URL changes (e.g., when navigating from practice).
  // For free users, do NOT sync a locked pillar from URL into state, or we overwrite
  // the redirect and cause an infinite loop (redirect → URL still old → sync restores locked pillar → redirect again).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pillarIdFromUrl = params.get('pillar');
    if (!pillarIdFromUrl) return;
    const numericPillarId = parseInt(pillarIdFromUrl, 10);
    if (isNaN(numericPillarId) || numericPillarId === activePillar) return;

    const isFreeUser = subscriptionStatus !== null && (subscriptionStatus?.isFree || !subscriptionStatus?.isActive);
    if (isFreeUser && grammarData?.pillars?.length) {
      const firstPillarId = grammarData.pillars[0]?.id;
      const urlPillarIndex = grammarData.pillars.findIndex(p => p.id === numericPillarId);
      if (urlPillarIndex !== 0 && firstPillarId != null) {
        // URL points to a locked pillar; do not sync (redirect effect will fix the URL)
        return;
      }
    }

    console.log(`🔄 URL pillar changed: ${activePillar} → ${numericPillarId}`);
    setActivePillar(numericPillarId);
  }, [location.search, activePillar, subscriptionStatus, grammarData]);

  // Tab title: same as category/topic name (e.g. "Konjunktiv II | Grammar | Logami"), else "Grammar | Logami"
  useEffect(() => {
    const topicTitle = userLevel && activePillar ? getGrammarTopicTitle(userLevel, activePillar) : null;
    const prev = document.title;
    document.title = topicTitle ? `${topicTitle} | Grammar | Logami` : 'Grammar | Logami';
    return () => { document.title = prev; };
  }, [userLevel, activePillar]);

  useEffect(() => {
  if (activePillar && grammarData?.pillars) {
    const pillar = grammarData.pillars.find(p => p.id === activePillar);

    if (pillar) {
      let allTasks = [];

      // Handle B2/C1 structure
      if (pillar.explanation?.categories) {
        allTasks = pillar.explanation.categories.flatMap(category =>
          category.practice?.flatMap(practiceLevel =>
            practiceLevel.tasks?.map(task => ({
              ...task,
              categoryId: category.id || category.name?.toLowerCase().replace(/\s+/g, '_'),
              categoryName: category.name,
              practiceLevel: practiceLevel.level
            })) || []
          ) || []
        );
      }
      // Handle A2/B1 structure
      else if (pillar.practice) {
        allTasks = pillar.practice.flatMap(level =>
          level.tasks?.map(task => ({
            ...task,
            practiceLevel: level.level,
            practiceLevelName: level.level
          })) || []
        );
      }

      // Get the first task to detect initial context
      if (allTasks.length > 0) {
        detectCurrentContext(activePillar, allTasks[0].id);
      }
    }
  }
}, [activePillar, grammarData, detectCurrentContext]);

  // Save progress to Firebase - level aware
  const saveProgress = async (pillarId, exerciseId, isCorrect) => {
    if (!user) return;

    try {
      const currentPillar = grammarData?.pillars?.find(p => p.id === pillarId);

      // Detect context for this exercise
      const exerciseContext = grammarProgressService.detectExerciseContext(
        grammarData,
        pillarId,
        exerciseId
      );

      // Use the new grammarProgressService method
      const result = {
        isCorrect,
        userAnswer: userAnswers[exerciseId] || '',
        correctAnswer: showFeedback[exerciseId]?.correctAnswer || '',
        timeSpent: 5, // estimated time per exercise
        context: exerciseContext // Pass context here
      };

      const success = await grammarProgressService.updateExerciseCompletion(
        user.uid,
        userLevel,
        pillarId,
        exerciseId,
        result
      );

      if (success) {
        console.log(`✅ Progress saved for ${pillarId}/${exerciseId} with context:`, exerciseContext);

        // Refresh progress data
        const updatedProgress = await grammarProgressService.getUserProgress(user.uid, userLevel);
        if (updatedProgress?.pillars) {
          setPillarProgress(updatedProgress.pillars);
        }
      }

      // Level-aware activity logging
      await logGrammarPractice({
        topicId: pillarId,
        topicName: currentPillar?.title || `Lesson ${pillarId}`,
        timeSpent: 5,
        correctAnswers: isCorrect ? 1 : 0,
        totalQuestions: 1,
        level: userLevel,
        exerciseType: 'interactive',
        difficulty: 'medium'
      });

      // Log pillar progress milestones
      const pillarProgressPercentage = getPillarProgress(pillarId);
      if (pillarProgressPercentage > 0 && pillarProgressPercentage % 25 === 0) {
        await logGrammarPillarProgress({
          level: userLevel,
          pillarId: pillarId,
          pillarName: currentPillar?.title || `Lesson ${pillarId}`,
          timeSpent: 5,
          progressPercentage: pillarProgressPercentage,
          exercisesCompleted: Object.values(pillarProgress[pillarId]?.exercises || {}).filter(e => e.completed).length,
          totalExercises: Object.keys(pillarProgress[pillarId]?.exercises || {}).length,
          pillarCompleted: pillarProgressPercentage === 100,
          pillarCategory: 'grammar'
        });
      }
    } catch (error) {
      console.error(`Error saving ${userLevel} progress:`, error);
    }
  };
  // Handle exercise answer
  const handleAnswer = (pillarId, taskId, answer, correctAnswers) => {
    const normalizedAnswer = answer.trim().toLowerCase().replace(/[.,!?;]/g, '');
    const correctArray = correctAnswers.split('|').map(s => s.trim().toLowerCase().replace(/[.,!?;]/g, ''));
    const isCorrect = correctArray.includes(normalizedAnswer);

    setUserAnswers(prev => ({ ...prev, [taskId]: answer }));
    setShowFeedback(prev => ({
      ...prev,
      [taskId]: {
        isCorrect,
        correctAnswer: correctAnswers.split('|')[0],
        userAnswer: answer
      }
    }));

    saveProgress(pillarId, taskId, isCorrect);
    return isCorrect;
  };

  // Check if pillar is unlocked
  const isPillarUnlocked = (pillarId) => {
    // ADMIN OVERRIDE: Admins have access to all pillars
    if (adminService.isAdmin()) {
      return true;
    }

    // If subscription status is not loaded yet, default to locked (except first pillar)
    if (subscriptionStatus === null) {
      const pillarIndex = grammarData?.pillars?.findIndex(p => p.id === pillarId);
      return pillarIndex === 0; // Only allow first pillar while loading
    }

    // Free users can only access the first pillar (id 1 or index 0)
    if (subscriptionStatus?.isFree || !subscriptionStatus?.isActive) {
      // Get pillar index
      const pillarIndex = grammarData?.pillars?.findIndex(p => p.id === pillarId);
      return pillarIndex === 0; // Only first pillar is unlocked
    }
    // Premium users have access to all pillars
    return true;
  };

  // Check if pillar is mastered - handles both B1 and B2 structures
  const isPillarMastered = (pillarId) => {
    const pillar = grammarData?.pillars?.find(p => p.id === pillarId);
    if (!pillar) return false;

    // Handle both B1 and B2 structures
    let allTasks = [];

    if (pillar.practice) {
      // B1 structure: pillar.practice[].tasks[]
      allTasks = pillar.practice?.flatMap(level => level.tasks || []) || [];
    } else if (pillar.explanation?.categories) {
      // B2 structure: pillar.explanation.categories[].practice[].tasks[]
      allTasks = pillar.explanation.categories.flatMap(category =>
        category.practice?.flatMap(practiceLevel => practiceLevel.tasks || []) || []
      ) || [];
    }

    if (allTasks.length === 0) return false;

    return allTasks.every(task =>
      pillarProgress[pillarId]?.exercises?.[task.id]?.completed
    );
  };

  // Calculate progress for a pillar - handles both B1 and B2 structures
  const getPillarProgress = (pillarId) => {
    const pillar = grammarData?.pillars?.find(p => p.id === pillarId);
    if (!pillar) return 0;

    // Handle both B1 and B2 structures
    let allTasks = [];

    if (pillar.practice) {
      // B1 structure: pillar.practice[].tasks[]
      allTasks = pillar.practice?.flatMap(level => level.tasks || []) || [];
    } else if (pillar.explanation?.categories) {
      // B2 structure: pillar.explanation.categories[].practice[].tasks[]
      allTasks = pillar.explanation.categories.flatMap(category =>
        category.practice?.flatMap(practiceLevel => practiceLevel.tasks || []) || []
      ) || [];
    }

    if (allTasks.length === 0) return 0;

    const completedTasks = allTasks.filter(task =>
      pillarProgress[pillarId]?.exercises?.[task.id]?.completed
    );

    return Math.round((completedTasks.length / allTasks.length) * 100);
  };

  const handleTabClick = (pillarId) => {
    if (isPillarUnlocked(pillarId)) {
      setActivePillar(pillarId);
      const levelPath = (level || userLevel).toString().toLowerCase();
      const newUrl = `${examPrefix}/lessons/${levelPath}?pillar=${pillarId}`;
      navigate(newUrl, { replace: true });
    } else {
      setShowPremiumModal(true);
    }
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <LogamiLogoSpinner size="xl" className="mx-auto mb-4" decorative={false} alt="Logami loading" />
          <p style={{ color: '#6b7280' }}>{t('grammar.loading', { level: userLevel })}.</p>
        </div>
      </div>
    );
  }

  // Handle case where grammar data failed to load
  if (!grammarData) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '2rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#374151', marginBottom: '1rem' }}>{t('grammar.error.title')}</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            {t('grammar.error.message', { level: userLevel })}
          </p>
          <button
            onClick={onBack}
            style={{
              backgroundColor: '#006D77',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Zurück zum Lernpfad
          </button>
        </div>
      </div>
    );
  }

  // Free plan: the one free grammar pass was used on a previous visit, block the module.
  const isFreeUserResolved = subscriptionStatus !== null && (subscriptionStatus?.isFree || !subscriptionStatus?.isActive) && !adminService.isAdmin();
  if (isFreeUserResolved && grammarAttemptUsed === true) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className={`${ELEVATED_SURFACE} max-w-sm w-full p-6 sm:p-8 text-center animate-fade-in-up`}>
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
              <Lock className="w-7 h-7 text-gray-900" />
            </div>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">
            {t('premium.required.grammar.attemptUsedTitle', { defaultValue: 'Your free grammar pass is used' })}
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            {t('premium.required.grammar.attemptUsedBody', { defaultValue: 'The free plan includes one pass of the first grammar module. Upgrade to Premium for unlimited access to all grammar lessons and exercises.' })}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/pricing')}
              className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl bg-gradient-to-r from-black via-gray-900 to-gray-700 text-white font-bold text-sm hover:shadow-[0_8px_20px_rgba(17,24,39,0.28)] active:scale-[0.98] touch-manipulation transition-all duration-300"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" />
              {t('premium.required.grammar.cta', { defaultValue: 'Unlock grammar lessons' })}
            </button>
            <button
              onClick={onBack}
              className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl border-2 border-black/10 bg-white text-gray-700 font-semibold text-sm hover:border-black/30 hover:bg-gray-50 touch-manipulation transition-all duration-300"
            >
              {t('common.goBack', { defaultValue: 'Back' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentPillar = grammarData?.pillars?.find(p => p.id === activePillar);
  const pillars = grammarData?.pillars || [];
  const currentPillarIndex = pillars.findIndex(p => p.id === activePillar);
  const canGoPrevPillar = currentPillarIndex > 0;
  const canGoNextPillar = currentPillarIndex < pillars.length - 1;

  const handlePrevPillar = () => {
    if (canGoPrevPillar) {
      const prevId = pillars[currentPillarIndex - 1].id;
      if (isPillarUnlocked(prevId)) {
        setActivePillar(prevId);
        navigate(`${examPrefix}/lessons/${(level || userLevel).toString().toLowerCase()}?pillar=${prevId}`, { replace: true });
      }
    }
  };

  const handleNextPillar = () => {
    if (canGoNextPillar) {
      const nextId = pillars[currentPillarIndex + 1].id;
      if (isPillarUnlocked(nextId)) {
        setActivePillar(nextId);
        navigate(`${examPrefix}/lessons/${(level || userLevel).toString().toLowerCase()}?pillar=${nextId}`, { replace: true });
      } else {
        setShowPremiumModal(true);
      }
    }
  };

  const handleGoToPractice = () => {
    navigate(`${examPrefix}/practice/grammar?pillar=${activePillar}`);
  };

  return (
  <div style={{
    minHeight: '100vh',
    backgroundColor: '#FFFFFF',
    padding: isMobile ? '0' : '1rem 2rem',
    paddingBottom: isMobile ? '6rem' : '1rem',
    fontSize: '1rem',
    lineHeight: '1.5',
    letterSpacing: '0.01em'
  }}>
    <div style={{ maxWidth: isMobile ? '100%' : '80rem', margin: '0 auto' }}>
       {/* Header with Tab Navigation */}
<div style={{ marginBottom: isMobile ? '0.5rem' : '2rem', padding: isMobile ? '0' : '0' }}>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? '0.5rem' : '1.5rem' }}>
    <button
      onClick={onBack}
      onMouseEnter={() => setIsBackHovered(true)}
      onMouseLeave={() => setIsBackHovered(false)}
      className="hidden sm:inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0 touch-manipulation"
    >
      {/* Wave fill effect (Right-to-Left) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: isBackHovered ? '100%' : '0%',
          background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
          transition: 'width 0.7s ease-out',
          zIndex: 0
        }}
      />
      <ArrowLeft
        className="w-5 h-5 relative z-10 transition-colors duration-500"
        style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
      />
      <span
        className="font-semibold relative z-10 transition-colors duration-500"
        style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
      >
        {t('grammar.back')}
      </span>
    </button>
  </div>

  {/* Tab Navigation */}
  <GrammarTabNavigation
    pillars={grammarData?.pillars || []}
    activePillar={activePillar}
    onTabClick={handleTabClick}
    isPillarUnlocked={() => true}
    isPillarPremium={(pillarId) => !isPillarUnlocked(pillarId)}
    isPillarMastered={isPillarMastered}
    getPillarProgress={getPillarProgress}
    isMobile={isMobile}
  />
</div>


        {/* Active Pillar Content */}
        {currentPillar && (
          <GrammarPillarRenderer
            pillar={currentPillar}
            userAnswers={userAnswers}
            showFeedback={showFeedback}
            onAnswer={handleAnswer}
            getPillarProgress={getPillarProgress}
            isPillarMastered={isPillarMastered}
            onOpenSectionsMenuRef={openSectionsMenuRef}
          />
        )}
      </div>

      {/* Bottom Navigation. Lesson View */}
      <GrammarBottomNav
        buttons={[
          {
            id: 'back',
            icon: ArrowLeft,
            label: t('grammar.back'),
            onClick: onBack
          },
          {
            id: 'home',
            icon: Home,
            label: 'Home',
            onClick: () => navigate('/dashboard')
          },
          {
            id: 'sections',
            icon: Grid,
            label: t('grammar.sections', { defaultValue: 'Sections' }),
            onClick: () => {
              if (openSectionsMenuRef.current) {
                openSectionsMenuRef.current();
              }
            }
          },
          {
            id: 'practice',
            icon: Dumbbell,
            label: t('grammar.practice', { defaultValue: 'Practice' }),
            onClick: handleGoToPractice,
            active: true,
            color: '#006D77'
          },
          {
            id: 'next-pillar',
            icon: ChevronRight,
            label: t('grammar.pillar.next', { defaultValue: 'Next Lesson' }),
            onClick: handleNextPillar,
            disabled: !canGoNextPillar
          }
        ]}
      />

      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-fade-in">
          <div className={`${ELEVATED_SURFACE} max-w-sm w-full p-6 sm:p-8 animate-slide-up`}>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100">
                  <Lock className="w-7 h-7 text-[#006D77]" />
                </div>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">{t('premium.required.grammar.title', { defaultValue: 'Master German grammar with confidence' })}</h2>
              <p className="text-sm text-gray-600 mb-6">
                {t('premium.required.grammar.body', { defaultValue: 'Advanced grammar lessons and exercises are available only for Premium subscribers. Upgrade now to unlock all grammar modules.' })}
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setShowPremiumModal(false);
                  }}
                  className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl bg-gradient-to-r from-[#00474f] via-[#006D77] to-[#007f8a] text-white font-bold text-sm hover:shadow-[0_8px_20px_rgba(0,109,119,0.35)] active:scale-[0.98] touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#006D77]/40"
                >
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  {t('premium.required.grammar.cta', { defaultValue: 'Unlock grammar lessons' })}
                </button>

                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl border-2 border-black/10 bg-white text-gray-700 font-semibold text-sm hover:border-black/30 hover:bg-gray-50 touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40"
                >
                  {t('premium.required.secondary')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrammarLearningContainer;